import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Handshake, ShieldAlert, Users, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function DealRoom() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("investors"); // investors | filmmakers

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.members({ limit: 100 });
        if (alive) setMembers(Array.isArray(data) ? data : []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const filmmakers = members.filter(m =>
    (m.departments || []).some(d => /direct|produc|writ|cinem/i.test(d)) ||
    /direct|produc|writ|cinem|filmmaker/i.test(m.headline || ""));
  const investors = members.filter(m =>
    /invest|financ|fund|exec/i.test(m.headline || "") ||
    (m.specialties || []).some(s => /invest|financ|fund/i.test(s)));
  const shown = tab === "investors" ? investors : filmmakers;

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden h-56 mb-8 border border-white/10">
        <img src="https://images.pexels.com/photos/6983543/pexels-photo-6983543.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600" className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-8">
          <div className="text-xs uppercase tracking-[0.3em] ff-accent-text mb-2">Deal Room</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">Investors meet filmmakers.</h1>
        </div>
      </div>

      {/* Compliance banner — SEC-safe */}
      <div className="ff-card p-5 mb-8 flex items-start gap-4 border-[hsl(var(--ff-accent)/0.25)]" data-testid="deal-room-compliance">
        <ShieldAlert className="w-5 h-5 ff-accent-text flex-shrink-0 mt-0.5" />
        <div className="text-sm text-neutral-300 leading-relaxed">
          <div className="font-semibold text-white">Networking only. No transactions.</div>
          The Deal Room is a directory for professional introductions. Film Front is <em>not</em> a broker-dealer, does not solicit
          investment, and does not offer or sell securities. Members are responsible for their own diligence and compliance
          with applicable securities laws.
        </div>
      </div>

      {/* Bento summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 ff-stagger">
        {[
          { label: "Investors", value: investors.length, icon: Sparkles },
          { label: "Filmmakers", value: filmmakers.length, icon: Users },
          { label: "Regions", value: "8+", icon: Handshake },
          { label: "Introductions this month", value: "—", icon: ArrowRight },
        ].map((s, i) => (
          <div key={i} className="ff-card p-6">
            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-3">
              <s.icon className="w-4 h-4 ff-accent-text" />
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400">{s.label}</div>
            <div className="font-display text-3xl tracking-tighter mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("investors")}
          className={`px-4 py-2 rounded-full text-sm uppercase tracking-widest transition-colors ${tab === "investors" ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
          data-testid="deal-room-tab-investors"
        >
          Investors
        </button>
        <button
          onClick={() => setTab("filmmakers")}
          className={`px-4 py-2 rounded-full text-sm uppercase tracking-widest transition-colors ${tab === "filmmakers" ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
          data-testid="deal-room-tab-filmmakers"
        >
          Filmmakers
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="ff-card p-10 text-center text-neutral-400" data-testid="deal-room-empty">
          No {tab} in the Deal Room yet. Check back soon — this is a growing directory.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 ff-stagger" data-testid="deal-room-grid">
          {shown.map((m) => (
            <div key={m.id} className="ff-card p-6" data-testid={`deal-room-card-${m.id}`}>
              <div className="w-12 h-12 rounded-full ff-accent-bg text-black font-semibold flex items-center justify-center mb-4">
                {m.avatar_url
                  ? <img src={m.avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
                  : (m.name || "?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div className="font-display text-lg tracking-tight">{m.name}</div>
              <div className="text-xs text-neutral-500 line-clamp-2 mt-1">{m.headline}</div>
              <button
                onClick={() => toast.success("Introduction request sent (use mobile app to message)")}
                className="mt-4 ff-btn-ghost text-xs w-full"
                data-testid={`deal-room-connect-${m.id}`}
              >
                Request introduction
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
